// @ts-check
// pwa/components/app-header.js — universal shell header (spec 003).
//
// Composes the Ocean header bar (wordmark + centred tab nav + theme + bell +
// avatar), the mobile second-bar, the profile sheet, and the sign-out modal.
// Rendered by pwa/lib/header.js into the persistent #app-header container on
// every $session paint. Honours the six-point contract (docs/UNIVERSAL_HEADER.md):
//   1 (theme moved to profile sheet)  2 #header-signout (profile-sheet footer)
//   3 data-nav <a> links    4 [data-admin-only] + body[data-is-admin]
//   5 --header-h height      6 aria-current="page" active styling

import { html } from 'lit-html';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
import { icon } from '../lib/icons.js';
import { tabNavTemplate } from './tab-nav.js';
import { bellTemplate, wireBell } from './notification-popover.js';
import { openHelpPopover, wireHelpPopover } from './help-popover.js';
import {
  profileSheetTemplate, wireProfileSheet, openProfileSheet, closeProfileSheet, userInitials,
} from './profile-sheet.js';
import { signoutModalTemplate, wireSignoutModal, openSignoutModal } from './signout-modal.js';
import { $unconfirmedCount } from '../lib/store.js';

/** F016: always-visible "N to confirm" badge. Links to /history (the durable home
 *  of attendance confirmation). Hidden when nothing is awaiting. Count from the
 *  $unconfirmedCount store (dashboard loadUnconfirmed → GET /api/me/unconfirmed-slots). */
function confirmBadgeTemplate() {
  const n = $unconfirmedCount.get();
  if (!n) return '';
  return html`
    <a href="/history" data-nav class="hdr-confirm-badge" title="${n} session${n === 1 ? '' : 's'} to confirm"
       aria-label="${n} session${n === 1 ? '' : 's'} to confirm">
      ${unsafeHTML(icon('clipboard-list', 'w-3.5 h-3.5'))} ${n} to confirm
    </a>`;
}

/**
 * @param {object} me  session user ({ preferred_name, first_name, last_name, email, is_admin })
 * @param {string} pathname
 * @returns {import('lit-html').TemplateResult}
 */
export function appHeaderTemplate(me, pathname) {
  return html`
    <header class="app-header bg-nc-ocean text-white px-4 sm:px-6 flex items-center gap-3 shadow-sm relative">
      <a href="/" data-wordmark class="flex items-center gap-2.5 min-w-0 text-white no-underline"
         aria-label="NCSLSC TAF Hub — home">
        ${unsafeHTML(icon('waves', 'w-5 h-5 text-nc-sand-yellow shrink-0'))}
        <div class="min-w-0 leading-tight">
          <p class="wordmark__eyebrow text-[0.65rem] uppercase tracking-[0.15em] text-white/65 leading-tight">NCSLSC</p>
          <h1 class="wordmark__route text-base sm:text-lg font-extrabold leading-tight truncate">TAF Hub</h1>
        </div>
      </a>
      ${tabNavTemplate({ pathname, variant: 'desktop' })}
      <div class="flex-1"></div>
      <div class="flex items-center gap-1 flex-shrink-0">
        ${confirmBadgeTemplate()}
        <a href="/roles" class="hbtn on-dark" aria-label="Roles & responsibilities" title="Roles & responsibilities">
          ${unsafeHTML(icon('network', 'w-5 h-5'))}
        </a>
        <button type="button" id="header-help-btn" class="hbtn on-dark"
                aria-label="Help &amp; Tutorials"
                title="Help &amp; Tutorials"
                aria-haspopup="dialog"
                aria-expanded="false">
          ${unsafeHTML(icon('circle-help', 'w-5 h-5'))}
        </button>
        ${bellTemplate()}
        <button type="button" class="hbtn hbtn-avatar on-dark" aria-label="Open your profile" data-profile-open data-tip="dash-profile">
          <span class="w-8 h-8 rounded-full bg-nc-sand-yellow border-2 border-white/30 flex items-center justify-center text-nc-ocean text-[0.72rem] font-extrabold select-none"
                data-user-initials aria-hidden="true">${userInitials(me)}</span>
        </button>
      </div>
    </header>
    ${tabNavTemplate({ pathname, variant: 'mobile' })}
    ${profileSheetTemplate(me)}
    ${signoutModalTemplate()}
  `;
}

/**
 * Wire the header's interactive surfaces. Called after every paint (lit-html
 * replaces the nodes, so listeners are re-attached fresh).
 * @param {HTMLElement} root  the #app-header container
 * @param {{ onSignout?: () => void }} opts
 */
export function wireAppHeader(root, { onSignout } = {}) {
  // Use .onclick (property assignment) NOT addEventListener for buttons that live
  // in the reused header DOM — lit-html reuses these elements across $session
  // re-paints so addEventListener stacks a new handler every time paint() runs.
  // .onclick replaces the previous handler atomically; no stacking, no double-fire.
  // (The bell component uses the _bellWired sentinel idiom for the same reason —
  // N-4 / "click doesn't open bell" — see notification-popover.js lines 265-274.)
  const profileBtn = /** @type {HTMLElement|null} */ (root.querySelector('[data-profile-open]'));
  if (profileBtn) profileBtn.onclick = () => openProfileSheet(root);

  const helpBtn = /** @type {HTMLElement|null} */ (root.querySelector('#header-help-btn'));
  if (helpBtn) helpBtn.onclick = () => openHelpPopover(helpBtn, null);

  wireBell(root);
  wireProfileSheet(root, {
    onSignoutTrigger: () => { closeProfileSheet(root); openSignoutModal(root); },
  });
  wireSignoutModal(root, () => onSignout?.());
}
